import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { vendorService } from '../services/vendorService';
import useUserLocation from './useUserLocation';

/**
 * Custom hook for browsing vendors with filters, sorting, and pagination
 */
export const useBrowseVendors = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userLocation, loading: locationLoading } = useUserLocation();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [priceMin, setPriceMin] = useState(1);
  const [priceMax, setPriceMax] = useState(1000);
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [sortBy, setSortBy] = useState('none');
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredVendor, setHoveredVendor] = useState(null);

  // Auto-populate location when detected or updated
  useEffect(() => {
    if (userLocation) {
      // Use address if available, otherwise use city/state
      const locationString = userLocation.address || 
                            (userLocation.city && userLocation.state ? `${userLocation.city}, ${userLocation.state}` : null) ||
                            userLocation.city || 
                            userLocation.state || 
                            null;
      
      // Always update location if we have a valid location string
      if (locationString) {
        setLocation(locationString);
      }
    }
  }, [userLocation]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    location,
    priceMin,
    priceMax,
    sortBy,
    selectedRating,
    selectedPriceRange,
  ]);

  // Build params with location-based filtering
  const params = useMemo(() => {
    const baseParams = {
      ...(searchTerm && { search: searchTerm }),
      ...(priceMin && priceMin > 1 && { min_price: priceMin }),
      ...(priceMax && priceMax < 1000 && { max_price: priceMax }),
      ...(selectedRating && selectedRating !== 'all' && { min_rating: parseFloat(selectedRating.replace('+', '')) }),
      per_page: 12,
      page: currentPage,
    };
    
    // Add location-based filtering and sorting
    if (userLocation) {
      // Priority: Use latitude/longitude for distance-based sorting (not filtering)
      if (userLocation.latitude && userLocation.longitude) {
        baseParams.latitude = userLocation.latitude;
        baseParams.longitude = userLocation.longitude;
        // Filter by country to prevent showing vendors from other countries
        if (userLocation.country) {
          baseParams.country = userLocation.country;
        }
        // Don't send radius - this way backend won't filter by distance, only sort
        // Use distance sorting when location is available
        if (sortBy === 'distance' || sortBy === 'none') {
          baseParams.sort_by = 'distance';
          baseParams.sort_order = 'asc';
        }
      } else if (userLocation.city) {
        baseParams.city = userLocation.city;
      }
      // Only use state if we don't have coordinates (to avoid conflicts)
      if (!userLocation.latitude && !userLocation.longitude && userLocation.state) {
        baseParams.state = userLocation.state;
      }
      // Add country filter even if we don't have coordinates
      if (userLocation.country && !baseParams.country) {
        baseParams.country = userLocation.country;
      }
    } else if (location) {
      // Fallback to city/state if coordinates not available
      const locationParts = location.split(', ');
      if (locationParts.length >= 2) {
        baseParams.city = locationParts[0];
        baseParams.state = locationParts[1];
      } else {
        baseParams.city = location;
      }
      // Still try to add country from userLocation if available
      if (userLocation?.country) {
        baseParams.country = userLocation.country;
      }
    }
    
    // Always add country from userLocation if available (highest priority)
    if (userLocation?.country && !baseParams.country) {
      baseParams.country = userLocation.country;
    }
    
    return baseParams;
  }, [searchTerm, priceMin, priceMax, selectedRating, currentPage, userLocation, location, sortBy]);

  // Handle other sort options
  const finalParams = useMemo(() => {
    const final = { ...params };
    
    // Handle other sort options (if not already set by distance)
    if (sortBy !== 'none' && sortBy !== 'distance' && !final.sort_by) {
      final.sort_by =
        sortBy === 'rating'
          ? 'rating'
          : sortBy === 'price_low' || sortBy === 'price_high'
          ? 'price'
          : 'created_at';
      final.sort_order = sortBy === 'price_low' ? 'asc' : 'desc';
    }
    
    return final;
  }, [params, sortBy]);

  // Only fetch when we have country information to filter by location
  const hasCountryFilter = !!(finalParams.country);
  const shouldFetch = hasCountryFilter && !locationLoading;

  // Invalidate and refetch when country becomes available
  useEffect(() => {
    if (hasCountryFilter && !locationLoading) {
      queryClient.invalidateQueries({ queryKey: ['public-vendors'] });
    }
  }, [hasCountryFilter, locationLoading, queryClient]);

  // Fetch vendors
  const { data: vendorsResp, isLoading } = useQuery({
    queryKey: ['public-vendors', finalParams],
    queryFn: async () => {
      const response = await vendorService.getVendors(finalParams);
      return response;
    },
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    keepPreviousData: false,
    enabled: shouldFetch,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });

  // Transform vendor data
  const vendors = useMemo(() => {
    if (isLoading) {
      return [];
    }
    
    const payload = vendorsResp?.data?.data || vendorsResp?.data || [];
    
    if (!payload || payload.length === 0) {
      return [];
    }
    
    const truncate = (str, n = 80) => {
      if (!str) return '';
      const s = String(str);
      return s.length > n ? s.slice(0, n) + '…' : s;
    };
    
    return payload.map((v) => ({
      id: v.id,
      name: v.business_name || 'Vendor',
      category: v.business_type || 'Business',
      categoryId: String(v.service_category_id || ''),
      rating: Number(v.average_rating || 0),
      reviewCount: v.total_reviews || 0,
      location: [v.city, v.state].filter(Boolean).join(', '),
      distance: '',
      price: 'View Services',
      priceRange: '',
      description: truncate(v.description || v.bio || '', 80),
      image: v.image_url || v.user?.profile_image_url || '',
      services: v.service_categories?.map(cat => cat.category_name).slice(0, 3) || [],
      serviceCount: v.service_count || v.vendor_services_count || (v.vendorServices?.length || 0),
      availability: v.is_online ? 'Online now' : '',
      featured: !!v.is_featured,
      vendorId: v.id,
      phone: v.phone_number || '',
      email: v.email || '',
      businessName: v.business_name || 'Vendor'
    }));
  }, [vendorsResp, isLoading]);

  // Pagination data
  const pagination = useMemo(() => {
    const paginationData = vendorsResp?.data || {};
    return {
      current_page: paginationData.current_page || 1,
      last_page: paginationData.last_page || 1,
      per_page: paginationData.per_page || 12,
      total: paginationData.total || 0,
    };
  }, [vendorsResp]);

  // Filter vendors - only by search term, not by location
  // Location filtering is handled by the backend API
  const filteredVendors = useMemo(() => {
    return vendors.filter((vendor) => {
      const matchesSearch =
        !searchTerm ||
        vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  }, [vendors, searchTerm]);

  // Sort vendors
  const sortedVendors = useMemo(() => {
    return [...filteredVendors].sort((a, b) => {
      // Featured vendors always at the top
      if (a.featured !== b.featured) {
        return b.featured ? 1 : -1;
      }
      
      // Then sort by selected criteria
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'price_low':
          return (
            parseInt(a.price.replace(/[^0-9]/g, '') || '0') -
            parseInt(b.price.replace(/[^0-9]/g, '') || '0')
          );
        case 'price_high':
          return (
            parseInt(b.price.replace(/[^0-9]/g, '') || '0') -
            parseInt(a.price.replace(/[^0-9]/g, '') || '0')
          );
        case 'reviews':
          return b.reviewCount - a.reviewCount;
        case 'none':
        default:
          return 0;
      }
    });
  }, [filteredVendors, sortBy]);

  // Clear all filters function
  const clearAllFilters = () => {
    setSearchTerm('');
    setLocation('');
    setPriceMin(1);
    setPriceMax(1000);
    setSelectedPriceRange('all');
    setSelectedRating('all');
    setSortBy('none');
    setCurrentPage(1);
    // Clear URL parameters
    navigate('/browse-vendors', { replace: true });
  };

  const loading = isLoading || locationLoading;

  return {
    // State
    searchTerm,
    setSearchTerm,
    location,
    setLocation,
    priceMin,
    setPriceMin,
    priceMax,
    setPriceMax,
    selectedPriceRange,
    setSelectedPriceRange,
    selectedRating,
    setSelectedRating,
    sortBy,
    setSortBy,
    currentPage,
    setCurrentPage,
    hoveredVendor,
    setHoveredVendor,
    
    // Data
    vendors: sortedVendors,
    pagination,
    loading,
    
    // Actions
    clearAllFilters,
    
    // Location
    userLocation,
    locationLoading,
  };
};

